import React, { useState } from "react";
import Modal from "./Modal";
import axios from "axios";
import { toast } from "react-toastify";
import { GiftIcon } from "@heroicons/react/24/outline";

export default function SubscribeToNewsletterModal({ show, hide }: { show: boolean; hide: () => void }) {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post("/api/newsletter/subscribe", { email });

      // Generate a promo code (you can customize this logic)
      const generatedPromoCode = "WELCOME5";
      setPromoCode(generatedPromoCode);
      setIsSubscribed(true);

      // Save to localStorage to prevent showing modal again
      localStorage.setItem("newsletter_subscribed", "true");
      localStorage.setItem("newsletter_email", email);
      localStorage.setItem("newsletter_promo_code", generatedPromoCode);

      toast.success("Successfully subscribed! Your promo code is ready.");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.errors?.email?.[0] ||
        error.response?.data?.message ||
        "Failed to subscribe. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // If user closes without subscribing, still mark as seen to not annoy them
    if (!isSubscribed) {
      localStorage.setItem("newsletter_subscribed", "dismissed");
    }
    setTimeout(() => {
      setEmail("");
      setPromoCode("");
      setIsSubscribed(false);
    }, 300);
    hide();
  };

  const copyPromoCode = () => {
    navigator.clipboard.writeText(promoCode);
    toast.success("Promo code copied to clipboard!");
  };

  return (
    <Modal title="" show={show} hide={handleClose} panelClassName="sm:max-w-md" bodyWrapperClassName="!pt-0">
      <div className="text-center">
        {!isSubscribed ? (
          <>
            {/* Icon Section */}
            <div className="flex justify-center items-center w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-purple-600">
              <GiftIcon className="w-10 h-10 text-white" aria-hidden="true" />
            </div>

            {/* Header */}
            <h3 className="mb-2 font-bold text-gray-900 text-2xl">Get Your Welcome 5% Discount!</h3>
            <p className="mb-6 text-gray-600">
              Subscribe to our newsletter and receive an exclusive{" "}
              <span className="font-semibold text-blue-600">5% welcome discount code</span> for our services.
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 border border-gray-300 focus:border-transparent rounded-lg focus:ring-2 focus:ring-blue-500 transition-all"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-blue-600 hover:from-blue-700 to-purple-600 hover:to-purple-700 disabled:opacity-50 shadow-lg hover:shadow-xl font-semibold text-white transition-all disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Subscribing..." : "Subscribe"}
              </button>
            </form>

            <p className="mt-4 text-gray-500 text-xs">
              By subscribing, you agree to receive promotional emails. You can unsubscribe at any time.
            </p>
          </>
        ) : (
          <>
            {/* Success State */}
            <div className="flex justify-center items-center w-16 h-16 mx-auto mb-3 rounded-full bg-green-100">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h3 className="mb-2 font-bold text-gray-900 text-xl">Welcome Aboard! 🎉</h3>
            <p className="mb-4 text-gray-600 text-sm">
              Thank you for subscribing! Here's your exclusive discount code:
            </p>

            {/* Promo Code Display */}
            <div className="mb-3 p-3 border-2 border-blue-400 border-dashed rounded-lg bg-gradient-to-br from-blue-50 to-purple-50">
              <p className="mb-1 text-gray-600 text-xs">Promo code</p>
              <div className="flex justify-center items-center space-x-2">
                <p className="font-bold text-blue-600 text-lg tracking-wider">{promoCode}</p>
                <button
                  onClick={copyPromoCode}
                  className="p-2 rounded hover:bg-blue-100 text-blue-600 hover:text-blue-700 transition-colors"
                  title="Copy to clipboard"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              </div>
              <p className="mt-1 text-gray-500 text-xs">5% welcome discount</p>
            </div>

            {/* Instructions */}
            <div className="mb-4 p-2 border border-yellow-200 rounded-lg bg-yellow-50 text-yellow-800 text-xs">
              📋 Use this code when requesting our services to get your 5% welcome discount.
            </div>

            <button
              onClick={handleClose}
              className="w-full px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 font-semibold text-white transition-colors"
            >
              Got it, Thanks!
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}
